/**
 * The printed route sheet's stand-in for the Delivery page's "Open Maps"
 * button: a QR code of the same Google Maps link, so the driver opens the
 * day's drive by pointing a phone at the paper (option 1 of the print canvas,
 * Abner, Sep 25). Built synchronously (`qrcode`'s `create`, already an
 * apps/ordo dependency) so the code is on the page before the print dialog
 * snapshots it: an async render could print an empty square.
 *
 * Pure; unit-tested.
 */

import { create } from 'qrcode'

export interface QrPath {
  /** Modules per side, without the quiet zone. */
  size: number
  /** One SVG path, a 1x1 square per dark module, in module units. */
  d: string
}

/**
 * The QR code of `text` as one SVG path, or null when it cannot be encoded
 * (empty, or longer than a QR code holds). Error correction L: a printed
 * sheet stays flat and clean, and the lowest level keeps a long Maps link
 * scannable at a small size.
 */
export function qrPath(text: string): QrPath | null {
  if (!text) {
    return null
  }
  try {
    const { modules } = create(text, { errorCorrectionLevel: 'L' })
    const parts: string[] = []
    for (let row = 0; row < modules.size; row++) {
      for (let col = 0; col < modules.size; col++) {
        if (modules.get(row, col)) {
          parts.push(`M${col} ${row}h1v1h-1z`)
        }
      }
    }
    return { size: modules.size, d: parts.join('') }
  } catch {
    return null
  }
}
